import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Chip, Tabs, Tab,
  ToggleButton, ToggleButtonGroup, Alert, Switch, TextField, MenuItem, Button, Tooltip, CircularProgress, Grid, IconButton,
} from '@mui/material';
import { Refresh, MonitorHeart, Visibility } from '@mui/icons-material';
import { LineChart } from '@mui/x-charts/LineChart';
import { useSnackbar } from 'notistack';
import api from '../../services/api';

// Compliance Monitor — statistics and ALERT-ONLY rules. This page cannot block, cap or suspend anything.
type Win = '1h' | '24h' | '7d';
const SEV_COLOR: any = { low: 'default', medium: 'warning', high: 'error', critical: 'error' };
const fmt = (v: any, d = 1) => (v === null || v === undefined ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));

const COLS: { key: string; label: string; tip: string; d?: number }[] = [
  { key: 'attempts', label: 'Attempts', tip: 'Outbound call attempts in the window', d: 0 },
  { key: 'asr_pct', label: 'ASR %', tip: 'Answered ÷ attempts' },
  { key: 'acd_sec', label: 'ACD s', tip: 'Average billable seconds per answered call' },
  { key: 'short_call_pct', label: 'Short %', tip: 'Answered calls under 30 s ÷ answered' },
  { key: 'caller_rotation_pct', label: 'CLI rotation %', tip: 'Distinct caller numbers ÷ attempts (100% = new caller every call)' },
  { key: 'unallocated_cli_pct', label: 'Unallocated CLI %', tip: 'Attempts whose caller number is not a DID assigned to the customer here' },
  { key: 'sequential_pct', label: 'Sequential %', tip: 'Destinations whose neighbour (+1) was dialled in the same hour ÷ distinct destinations' },
  { key: 'peak_cps', label: 'Peak CPS', tip: 'Highest attempts in a single second', d: 0 },
  { key: 'spend', label: 'Spend $', tip: 'Sell value of the window', d: 2 },
  { key: 'spend_spike', label: 'Spike ×', tip: '24h spend ÷ previous 7-day daily average (24h window only)', d: 2 },
  { key: 'offhours_pct', label: 'Off-hours %', tip: 'Attempts outside 08:00–21:00 destination local time (approximate by country)' },
  { key: 'code_603', label: '603', tip: 'Carrier screening declines', d: 0 },
  { key: 'code_404_pct', label: '404 %', tip: 'Invalid / unallocated destinations ÷ attempts' },
  { key: 'code_503', label: '503', tip: 'Congestion rejections', d: 0 },
];

export const ComplianceMonitorPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState(0);
  const [win, setWin] = useState<Win>(() => (localStorage.getItem('ce.monitor.win') as Win) || '1h');
  const [ov, setOv] = useState<any>(null);
  const [rules, setRules] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [tl, setTl] = useState<any[]>([]);
  const [tlTenant, setTlTenant] = useState<string>('');
  const [tlHours, setTlHours] = useState<number>(168);
  const [loading, setLoading] = useState(false);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/monitor/overview?window=${win}`); setOv(data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, [win]);
  const loadRules = useCallback(async () => { try { const { data } = await api.get('/monitor/rules'); setRules(data); } catch (e) { console.error(e); } }, []);
  const loadAlerts = useCallback(async () => { try { const { data } = await api.get('/monitor/alerts?limit=200'); setAlerts(data); } catch (e) { console.error(e); } }, []);
  const loadTimeline = useCallback(async () => {
    if (!tlTenant) return;
    try { const { data } = await api.get(`/monitor/timeline/${tlTenant}?hours=${tlHours}`); setTl(data); } catch (e) { console.error(e); }
  }, [tlTenant, tlHours]);

  useEffect(() => { localStorage.setItem('ce.monitor.win', win); loadOverview(); }, [win, loadOverview]);
  useEffect(() => { loadRules(); loadAlerts(); }, [loadRules, loadAlerts]);
  useEffect(() => { loadTimeline(); }, [loadTimeline]);
  useEffect(() => { if (!tlTenant && ov?.customers?.length) setTlTenant(ov.customers[0].tenant_id); }, [ov, tlTenant]);

  const customers = ov?.customers || [];
  const openAlerts = alerts.filter((a) => !a.acknowledged).length;

  const patchRule = async (r: any, dto: any) => {
    try { await api.patch(`/monitor/rules/${r.id}`, dto); await loadRules(); await loadOverview();
      enqueueSnackbar(dto.enabled !== undefined ? `${r.name}: ${dto.enabled ? 'alerts ON' : 'alerts OFF'} (alert-only)` : `${r.name} updated`, { variant: 'success' }); }
    catch (e: any) { enqueueSnackbar(e.response?.data?.message || 'Update failed', { variant: 'error' }); }
  };
  const ack = async (id: string) => { await api.patch(`/monitor/alerts/${id}/acknowledge`); loadAlerts(); };

  const series = useMemo(() => {
    const x = tl.map((r) => new Date(r.hour));
    return { x,
      attempts: tl.map((r) => Number(r.attempts)), asr: tl.map((r) => Number(r.asr_pct ?? 0)),
      acd: tl.map((r) => Number(r.acd_sec ?? 0)), cps: tl.map((r) => Number(r.peak_cps)),
      unalloc: tl.map((r) => Number(r.unallocated_cli)), seq: tl.map((r) => Number(r.sequential_dsts)), c603: tl.map((r) => Number(r.code_603)) };
  }, [tl]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MonitorHeart sx={{ color: 'primary.main', fontSize: 32 }} />
          <Box>
            <Typography variant='h5' fontWeight={700}>Compliance Monitor</Typography>
            <Typography variant='body2' color='text.secondary'>Per-customer traffic health for fraud, spam and robocall oversight — statistics and alerts only</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup size='small' exclusive value={win} onChange={(_, v) => v && setWin(v)}>
            <ToggleButton value='1h'>Last hour</ToggleButton><ToggleButton value='24h'>24 hours</ToggleButton><ToggleButton value='7d'>7 days</ToggleButton>
          </ToggleButtonGroup>
          <IconButton onClick={() => { loadOverview(); loadRules(); loadAlerts(); loadTimeline(); }}><Refresh /></IconButton>
        </Box>
      </Box>

      <Alert severity='info' icon={<Visibility />} sx={{ mb: 2 }}>
        <b>Monitoring only.</b> Nothing on this page blocks calls, caps capacity or suspends a customer. Rules are off by default; when a person switches
        one on it only records an alert. Enforcement is always a separate, manual decision.
      </Alert>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { l: 'Customers with traffic', v: customers.length },
          { l: 'Rules on (alert-only)', v: `${ov?.rules_enabled ?? 0} / ${ov?.rules_total ?? 0}` },
          { l: 'Open alerts', v: openAlerts },
          { l: 'Last refresh', v: ov?.state?.last_refresh ? new Date(ov.state.last_refresh).toLocaleTimeString() : '—' },
        ].map((c) => (
          <Grid key={c.l} size={{ xs: 6, md: 3 }}>
            <Card><CardContent sx={{ py: 1.5 }}><Typography variant='caption' color='text.secondary'>{c.l}</Typography><Typography variant='h6' fontWeight={700}>{c.v}</Typography></CardContent></Card>
          </Grid>
        ))}
      </Grid>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label='Overview' /><Tab label='Timeline' /><Tab label={`Rules (${rules.filter((r) => r.enabled).length} on)`} /><Tab label={`Alerts (${openAlerts})`} />
      </Tabs>

      {tab === 0 && (
        <Card>
          <Box sx={{ overflowX: 'auto' }}>
            {loading && !ov ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
              <Table size='small'>
                <TableHead><TableRow>
                  <TableCell>Customer</TableCell>
                  {COLS.map((c) => <TableCell key={c.key} align='right'><Tooltip title={c.tip}><span>{c.label}</span></Tooltip></TableCell>)}
                  <TableCell>Would flag</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {customers.map((r: any) => {
                    const hit = new Set((r.breaches || []).map((b: any) => COLS.find((c) => rules.find((x) => x.key === b.key)?.metric === c.key)?.key));
                    return (
                      <TableRow key={r.tenant_id} hover>
                        <TableCell><b>{r.tenant_name}</b>{r.tenant_status !== 'active' && <Chip size='small' label={r.tenant_status} sx={{ ml: 1 }} />}</TableCell>
                        {COLS.map((c) => <TableCell key={c.key} align='right' sx={hit.has(c.key) ? { color: 'error.main', fontWeight: 700 } : {}}>{fmt(r[c.key], c.d)}</TableCell>)}
                        <TableCell>
                          {(r.breaches || []).map((b: any) => (
                            <Tooltip key={b.key} title={b.enabled ? 'Rule ON — alert recorded' : 'Rule OFF — shown for information only'}>
                              <Chip size='small' label={b.name} color={b.enabled ? SEV_COLOR[b.severity] : 'default'} variant={b.enabled ? 'filled' : 'outlined'} sx={{ mr: 0.5, mb: 0.5 }} />
                            </Tooltip>))}
                        </TableCell>
                      </TableRow>);
                  })}
                  {!customers.length && <TableRow><TableCell colSpan={COLS.length + 2} align='center'>No outbound traffic in this window</TableCell></TableRow>}
                </TableBody>
              </Table>)}
          </Box>
          <Typography variant='caption' color='text.secondary' sx={{ p: 1.5, display: 'block' }}>
            Red values breach a rule threshold for this window. Outlined chips mean the rule is switched off and nothing was recorded. “Last hour” covers the current and previous clock hour.
          </Typography>
        </Card>
      )}

      {tab === 1 && (
        <Card><CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField select size='small' label='Customer' value={tlTenant} onChange={(e) => setTlTenant(e.target.value)} sx={{ minWidth: 240 }}>
              {customers.map((c: any) => <MenuItem key={c.tenant_id} value={c.tenant_id}>{c.tenant_name}</MenuItem>)}
            </TextField>
            <TextField select size='small' label='Range' value={tlHours} onChange={(e) => setTlHours(Number(e.target.value))}>
              <MenuItem value={24}>24 hours</MenuItem><MenuItem value={72}>3 days</MenuItem><MenuItem value={168}>7 days</MenuItem><MenuItem value={720}>30 days</MenuItem>
            </TextField>
          </Box>
          {tl.length ? (<>
            <Typography variant='subtitle2'>Attempts and peak CPS per hour</Typography>
            <LineChart height={260} xAxis={[{ data: series.x, scaleType: 'time' }]}
              series={[{ data: series.attempts, label: 'Attempts', showMark: false }, { data: series.cps, label: 'Peak CPS', showMark: false, yAxisId: 'r' }]}
              yAxis={[{ id: 'l' }, { id: 'r', position: 'right' }]} />
            <Typography variant='subtitle2'>Answer rate % and ACD seconds</Typography>
            <LineChart height={240} xAxis={[{ data: series.x, scaleType: 'time' }]}
              series={[{ data: series.asr, label: 'ASR %', showMark: false }, { data: series.acd, label: 'ACD s', showMark: false, yAxisId: 'r' }]}
              yAxis={[{ id: 'l' }, { id: 'r', position: 'right' }]} />
            <Typography variant='subtitle2'>Caller-ID and pattern indicators per hour</Typography>
            <LineChart height={240} xAxis={[{ data: series.x, scaleType: 'time' }]}
              series={[{ data: series.unalloc, label: 'Unallocated CLI', showMark: false }, { data: series.seq, label: 'Sequential dsts', showMark: false }, { data: series.c603, label: '603 declines', showMark: false }]} />
          </>) : <Typography color='text.secondary'>No hourly data for this customer yet.</Typography>}
        </CardContent></Card>
      )}

      {tab === 2 && (
        <Card>
          <Alert severity='warning' sx={{ m: 2 }}>Switching a rule on makes it <b>record alerts only</b>. It never blocks, caps or suspends. Thresholds can be tuned here; changes apply at the next 5-minute evaluation.</Alert>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small'>
              <TableHead><TableRow><TableCell>Alerts</TableCell><TableCell>Rule</TableCell><TableCell>Condition</TableCell><TableCell>Window</TableCell><TableCell>Min attempts</TableCell><TableCell>Severity</TableCell><TableCell>Scope</TableCell><TableCell>Description</TableCell></TableRow></TableHead>
              <TableBody>
                {rules.map((r) => <RuleRow key={r.id} r={r} onPatch={(dto) => patchRule(r, dto)} />)}
              </TableBody>
            </Table>
          </Box>
        </Card>
      )}

      {tab === 3 && (
        <Card><Box sx={{ overflowX: 'auto' }}>
          <Table size='small'>
            <TableHead><TableRow><TableCell>Time</TableCell><TableCell>Customer</TableCell><TableCell>Rule</TableCell><TableCell>Severity</TableCell><TableCell>Value</TableCell><TableCell>Detail</TableCell><TableCell /></TableRow></TableHead>
            <TableBody>
              {alerts.map((a) => (
                <TableRow key={a.id} hover sx={a.acknowledged ? { opacity: 0.6 } : {}}>
                  <TableCell>{new Date(a.created_at).toLocaleString()}</TableCell>
                  <TableCell>{a.tenant_name}</TableCell>
                  <TableCell>{a.alert_type.replace('monitor:', '')}</TableCell>
                  <TableCell><Chip size='small' label={a.severity} color={SEV_COLOR[a.severity]} /></TableCell>
                  <TableCell>{a.data?.value !== undefined ? `${fmt(a.data.value, 2)} ${a.data.metric?.endsWith('_pct') ? '%' : ''} (limit ${a.data.threshold})` : '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 420 }}>{a.description}</TableCell>
                  <TableCell>{!a.acknowledged && <Button size='small' onClick={() => ack(a.id)}>Acknowledge</Button>}</TableCell>
                </TableRow>))}
              {!alerts.length && <TableRow><TableCell colSpan={7} align='center'>No alerts recorded. Rules are off, or nothing has crossed a threshold.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Box></Card>
      )}
    </Box>
  );
};

const RuleRow: React.FC<{ r: any; onPatch: (dto: any) => void }> = ({ r, onPatch }) => {
  const [thr, setThr] = useState(String(r.threshold));
  const [min, setMin] = useState(String(r.min_attempts));
  useEffect(() => { setThr(String(r.threshold)); setMin(String(r.min_attempts)); }, [r]);
  const dirty = thr !== String(r.threshold) || min !== String(r.min_attempts);
  return (
    <TableRow hover sx={r.enabled ? { bgcolor: 'action.selected' } : {}}>
      <TableCell><Switch size='small' checked={!!r.enabled} onChange={(e) => onPatch({ enabled: e.target.checked })} /></TableCell>
      <TableCell><b>{r.name}</b><br /><Typography variant='caption' color='text.secondary'>{r.key}</Typography></TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.metric} {r.op} <TextField size='small' value={thr} onChange={(e) => setThr(e.target.value)} sx={{ width: 90 }} inputProps={{ style: { padding: '4px 8px' } }} /></TableCell>
      <TableCell>{r.win}</TableCell>
      <TableCell><TextField size='small' value={min} onChange={(e) => setMin(e.target.value)} sx={{ width: 80 }} inputProps={{ style: { padding: '4px 8px' } }} /></TableCell>
      <TableCell>
        <TextField select size='small' value={r.severity} onChange={(e) => onPatch({ severity: e.target.value })} sx={{ width: 110 }}>
          {['low', 'medium', 'high', 'critical'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
      </TableCell>
      <TableCell>{r.tenant_name || 'All customers'}</TableCell>
      <TableCell sx={{ maxWidth: 360 }}>
        <Typography variant='caption'>{r.description}</Typography>
        {dirty && <Box sx={{ mt: 0.5 }}><Button size='small' variant='outlined' onClick={() => onPatch({ threshold: thr, min_attempts: min })}>Save</Button></Box>}
      </TableCell>
    </TableRow>
  );
};
